import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo, Wordmark } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { LogIn, Loader2, Eye, ShieldCheck, KeyRound } from "lucide-react";
import { useLang } from "@/i18n";
import { ProgereDownloadAppButton } from "@/components/shared/ProgereDownloadAppButton";

export default function Login() {
  const { login, register, demoLogin, verify2fa } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [error, setError] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [method, setMethod] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      if (mode === "login") {
        const res = await login(form.email, form.password);
        if (res?.twofa_required) {
          setChallenge(res);
          setMethod(res.methods[0]);
          if (res.dev_code) setDevCode(res.dev_code);
          setLoading(false);
          return;
        }
        toast.success("Connexion réussie");
        navigate("/aujourdhui");
      } else {
        await register(form);
        toast.success("Compte créé ! Choisissez votre forfait pour activer votre accès.");
        navigate("/abonnements");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await verify2fa({ challenge_id: challenge.challenge_id, method, code });
      toast.success("Vérification réussie");
      navigate("/aujourdhui");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const resendEmail = async () => {
    try {
      const { data } = await api.post("/auth/2fa/challenge/send", { challenge_id: challenge.challenge_id, method: "email" });
      if (data.dev_code) setDevCode(data.dev_code);
      toast.success(t("Renvoyer le code"));
    } catch { toast.error("Erreur"); }
  };

  const tryDemo = async () => {
    setDemoLoading(true); setError("");
    try { await demoLogin(); toast.success("Bienvenue dans la démo PROGÈRE CRM"); navigate("/aujourdhui"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setDemoLoading(false); }
  };

  if (challenge) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#07080A]"
        style={{ backgroundImage: "radial-gradient(circle at 50% 0%, rgba(245,197,24,0.07), transparent 55%)" }}>
        <div className="w-full max-w-md bg-[#0e0e0e] border border-white/10 rounded-[28px] p-8 sm:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.6)] pg-fade-up" data-testid="twofa-card">
          <div className="flex flex-col items-center">
            <ShieldCheck size={40} className="text-[#F2BC2E]" />
            <h1 className="text-2xl font-display font-bold text-white mt-3">{t("Vérification en deux étapes")}</h1>
            <p className="text-sm text-gray-400 mt-2 text-center">
              {method === "email" ? `${t("Entrez le code envoyé par courriel")} (${challenge.email_hint})` : t("Entrez le code de votre application d'authentification")}
            </p>
          </div>
          {challenge.methods.length > 1 && (
            <div className="flex gap-2 mt-5">
              {challenge.methods.map((m) => (
                <button key={m} data-testid={`twofa-method-${m}`} onClick={() => setMethod(m)}
                  className={`flex-1 rounded-xl border py-2 text-xs font-semibold transition-colors ${method === m ? "border-[#F2BC2E] text-[#F2BC2E] bg-[#F2BC2E]/10" : "border-white/10 text-gray-400"}`}>
                  {m === "totp" ? t("Application d'authentification") : t("Code par courriel")}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={verify} className="mt-6 space-y-4">
            <input value={code} onChange={(e) => setCode(e.target.value)} data-testid="twofa-code-input" placeholder="000000" inputMode="numeric"
              className="w-full text-center tracking-[0.5em] text-2xl bg-[#151515] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#F2BC2E] focus:outline-none" />
            {devCode && <p className="text-xs text-[#F2BC2E] text-center" data-testid="dev-code">Mode dev — code : <b>{devCode}</b></p>}
            {error && <p className="text-sm text-[#F10D19]" data-testid="twofa-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="twofa-verify-btn" className="w-full btn-gold rounded-xl py-3.5 flex items-center justify-center gap-2 text-sm">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} {t("Vérifier")}
            </button>
          </form>
          <div className="flex justify-between mt-4 text-xs">
            {method === "email" && <button onClick={resendEmail} className="text-[#F2BC2E] hover:underline" data-testid="twofa-resend">{t("Renvoyer le code")}</button>}
            <button onClick={() => setMethod("backup")} className="text-gray-400 hover:underline ml-auto" data-testid="twofa-backup">{t("Utiliser un code de secours")}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#07080A]"
      style={{ backgroundImage: "radial-gradient(circle at 50% 0%, rgba(245,197,24,0.07), transparent 55%)" }}>
      <div className="w-full max-w-md bg-[#0e0e0e] border border-white/10 rounded-[28px] p-8 sm:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.6)] pg-fade-up relative" data-testid="login-card">
        <div className="absolute top-4 right-4"><ThemeToggle /></div>
        <div className="flex flex-col items-center">
          <Logo className="h-24" />
          <Wordmark className="text-3xl mt-4" />
          <p className="text-sm text-gray-400 mt-2 text-center">CRM intelligent pour la construction au Québec.</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-4">
          {mode === "register" && (
            <>
              <Field label={t("NOM COMPLET")} testid="name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Jean Tremblay" required />
              <Field label={t("ENTREPRISE")} testid="company-input" value={form.company} onChange={(v) => setForm({ ...form, company: v })} placeholder="Votre entreprise" />
            </>
          )}
          <Field label={t("COURRIEL")} type="email" testid="email-input" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="vous@entreprise.ca" required />
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] font-bold tracking-wider text-gray-400">{t("MOT DE PASSE")}</label>
              <button type="button" onClick={() => toast.info("Contactez votre administrateur pour réinitialiser le mot de passe.")} className="text-xs text-[#F2BC2E] hover:underline">{t("Mot de passe oublié ?")}</button>
            </div>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="password-input" placeholder="••••••••"
              className="w-full bg-[#151515] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:border-[#F2BC2E] focus:outline-none transition-colors" />
          </div>

          {error && <p className="text-sm text-[#F10D19]" data-testid="auth-error">{error}</p>}

          <button type="submit" disabled={loading} data-testid="submit-auth-btn"
            className="w-full btn-gold rounded-xl py-3.5 flex items-center justify-center gap-2 text-sm">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
            {mode === "login" ? t("Se connecter") : t("Créer mon compte")}
          </button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-white/10" /><div className="flex-1 h-px bg-white/10" />
        </div>

        <button onClick={tryDemo} disabled={demoLoading} data-testid="try-demo-btn"
          className="w-full rounded-xl border border-white/15 text-white py-3.5 flex items-center justify-center gap-2 text-sm font-semibold hover:bg-white/5 transition-colors">
          {demoLoading ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
          {t("Découvrir la démo en direct")}
        </button>
        <p className="text-center text-xs text-gray-500 mt-3">Explorez PROGÈRE CRM avec des données de démonstration.</p>

        <div className="mt-4"><ProgereDownloadAppButton variant="login" size="sm" moduleCode="crm" /></div>

        <p className="mt-5 text-center text-sm text-gray-500">
          {mode === "login" ? "Pas de compte ?" : "Déjà inscrit ?"}{" "}
          <button data-testid="toggle-auth-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="text-[#F2BC2E] font-semibold hover:underline">
            {mode === "login" ? t("Créer mon compte") : t("Se connecter")}
          </button>
        </p>

        <div className="mt-6 pt-5 border-t border-white/5 text-center space-y-2">
          <a href="/portail" data-testid="portal-access-link" className="text-xs text-gray-400 hover:text-[#F2BC2E] transition-colors">Vous êtes un client ? Accéder à l'espace client →</a>
          <p className="text-xs text-gray-600">PROGÈRE — Propulsé par <span className="text-[#F2BC2E]">Groupe MJR Construction Inc.</span></p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, required, placeholder }) {
  return (
    <div>
      <label className="block text-[11px] font-bold tracking-wider text-gray-400 mb-1.5">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} placeholder={placeholder}
        className="w-full bg-[#151515] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:border-[#F2BC2E] focus:outline-none transition-colors" />
    </div>
  );
}
